import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import ScaleToFit from "./ScaleToFit";
import SideStarField from "./SideStarField";
import { useIsPortrait } from "./useIsPortrait";
import HTLDSlide2Mobile from "./HTLDSlide2Mobile";
import { useVisibleAnimation } from "./useVisibleAnimation";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";
import { pick } from "i18n/lang";
import { slide2Content, slide2HighlightSanskrit, useSlide2, Seg } from "./slide2.data";

const accent = "#7f77dd";
const font   = FONT_BODY;

const S: Record<string, CSSProperties> = {
root: {
background: "#040f18", fontFamily: font, color: "#d0e8f8",
width: "100%", height: "100%",
display: "flex", flexDirection: "column",
boxSizing: "border-box",
overflow: "hidden",
},
header: {
display: "flex", alignItems: "center", justifyContent: "space-between",
padding: "5px clamp(12px,2vw,24px)",
background: "#071520", borderBottom: "1px solid rgba(127,119,221,0.22)",
position: "relative", flexShrink: 0,
},
logo:        { display: "flex", alignItems: "center", gap: "clamp(6px,1vw,12px)", zIndex: 1 },
logoIcon:    {
width: "clamp(24px,3.5vw,50px)",
height: "clamp(24px,3.5vw,50px)",
background: "#0c2a44",
border: "1px solid rgba(55,138,221,0.35)",
borderRadius: "50%",
display: "flex",
alignItems: "center",
justifyContent: "center",
fontSize: "clamp(11px,1.2vw,15px)",
overflow: "hidden"
},
logoName:    { fontSize: "clamp(10px,0.9vw,13px)", fontWeight: 600, letterSpacing: 3, color: accent, textTransform: "uppercase", fontFamily: FONT_MONO },
headerTitle: { fontSize: "clamp(17px,2.0vw,28px)", fontStyle: "normal", fontWeight: 600, color: "#e8f4ff", letterSpacing: 0.5, zIndex: 1, fontFamily: FONT_DISPLAY, paddingRight: "0.25em" },
// Header row now holds the breadcrumb (left) AND the slide title (right), above the line
subtitleBar: {
background: "#050f1a", borderBottom: "1px solid rgba(127,119,221,0.1)",
padding: "clamp(5px,0.6vw,9px) clamp(16px,3vw,40px)",
display: "flex", alignItems: "center", justifyContent: "space-between",
gap: "clamp(12px,2vw,28px)", flexShrink: 0,
},
body: {
flex: 1, minHeight: 0,
display: "flex", gap: "clamp(16px,2vw,28px)",
padding: "clamp(8px,1.0vw,12px) clamp(16px,3vw,40px) clamp(8px,0.9vw,10px)",
alignItems: "stretch",
overflow: "hidden",
},
content: {
flex: 1, minWidth: "min(100%, 380px)",
display: "flex", flexDirection: "column",
gap: "clamp(5px,0.7vw,9px)",
justifyContent: "flex-start",
minHeight: 0,
},
panel: {
width: "clamp(220px,28vw,380px)", flexShrink: 0,
display: "flex", flexDirection: "column",
},
};

// ─── Hindu Animated Canvas ───────────────────────────────────────────────────
const HinduCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawFnRef = useRef<null | (() => void)>(null);
  const dtRef     = useRef<number>(0);
  
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d") as CanvasRenderingContext2D; if (!ctx) return;
    const W = 420, H = 560; canvas.width = W; canvas.height = H;
    const cx = 210, cy = 268;
    let frame = 0;
  
    const C = "127,119,221";
    const c = pick(slide2Content).canvas; // slides remount on language change

    const stars = Array.from({ length: 110 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      r: 0.3 + Math.random() * 1.4,
      tw: Math.random() * Math.PI * 2, spd: 0.010 + Math.random() * 0.024,
    }));
  
    const AUM = [
      { letter: "A", label: c.aumLabels[0], idx: 0, sz: 8 },
      { letter: "U", label: c.aumLabels[1], idx: 1, sz: 7 },
      { letter: "M", label: c.aumLabels[2], idx: 2, sz: 6 },
      { letter: "∞", label: c.aumLabels[3], idx: 3, sz: 9 },
    ];
    const TRAIL = 20;
    const trails = AUM.map(() => [] as {x:number,y:number}[]);
  
    const particles = Array.from({ length: 30 }, () => ({
      angle:  Math.random() * Math.PI * 2,
      radius: 75 + Math.random() * 120,
      spd:    (0.002 + Math.random() * 0.004) * (Math.random() < 0.5 ? 1 : -1),
      r:      0.5 + Math.random() * 1.3,
      tw:     Math.random() * Math.PI * 2,
      tws:    0.012 + Math.random() * 0.026,
    }));
  
    function drawPetal(
      px: number, py: number, angle: number,
      len: number, alpha: number,
    ) {
      ctx.save();
      ctx.translate(px, py); ctx.rotate(angle);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.bezierCurveTo(-len*0.22,-len*0.35,-len*0.22,-len*0.85, 0,-len);
      ctx.bezierCurveTo( len*0.22,-len*0.85, len*0.22,-len*0.35, 0, 0);
      const pg = ctx.createLinearGradient(0, 0, 0, -len);
      pg.addColorStop(0,   `rgba(${C},${alpha*0.45})`);
      pg.addColorStop(0.5, `rgba(${C},${alpha})`);
      pg.addColorStop(1,   `rgba(${C},${alpha*0.25})`);
      ctx.fillStyle = pg; ctx.fill();
      ctx.strokeStyle = `rgba(190,185,255,${alpha*0.50})`; ctx.lineWidth = 0.8; ctx.stroke();
      ctx.restore();
    }
  
    function drawMandalaRing(r: number, segs: number, rot: number, alpha: number) {
      for (let i = 0; i < segs; i++) {
        const a  = rot + (i/segs)*Math.PI*2;
        const a2 = rot + ((i+0.5)/segs)*Math.PI*2;
        ctx.beginPath();
        ctx.moveTo(cx+(r-10)*Math.cos(a), cy+(r-10)*Math.sin(a));
        ctx.lineTo(cx+ r    *Math.cos(a), cy+ r    *Math.sin(a));
        ctx.lineTo(cx+(r+14)*Math.cos(a2),cy+(r+14)*Math.sin(a2));
        ctx.closePath();
        ctx.fillStyle = `rgba(${C},${alpha})`; ctx.fill();
        ctx.strokeStyle = `rgba(180,175,255,${alpha*0.45})`; ctx.lineWidth=0.5; ctx.stroke();
      }
    }
  
    function drawHexagram(r: number, rot: number, alpha: number) {
      [0, Math.PI/3].forEach(offset => {
        ctx.beginPath();
        for (let i = 0; i < 3; i++) {
          const a = rot + offset + (i/3)*Math.PI*2;
          const x = cx + r*Math.cos(a), y = cy + r*Math.sin(a);
          i === 0 ? ctx.moveTo(x,y) : ctx.lineTo(x,y);
        }
        ctx.closePath();
        ctx.strokeStyle = `rgba(185,180,255,${alpha})`; ctx.lineWidth = 1; ctx.stroke();
        ctx.fillStyle = `rgba(${C},${alpha*0.08})`; ctx.fill();
      });
    }
  
    function draw() {
      ctx.clearRect(0, 0, W, H);
  
      const bgGrd = ctx.createRadialGradient(cx, cy*0.9, 0, cx, cy, W);
      bgGrd.addColorStop(0, "#080416"); bgGrd.addColorStop(0.5, "#040212"); bgGrd.addColorStop(1, "#010108");
      ctx.fillStyle = bgGrd; ctx.fillRect(0, 0, W, H);
  
      ([
        [cx,  cy,   200, `rgba(60,40,165,0.20)`],
        [45,  55,   125, `rgba(80,22,145,0.15)`],
        [385, 72,   105, `rgba(48,15,130,0.13)`],
        [40,  495,  135, `rgba(65,30,160,0.12)`],
        [392, 485,  115, `rgba(40,10,125,0.11)`],
      ] as [number,number,number,string][]).forEach(([nx,ny,nr,nc]) => {
        const g = ctx.createRadialGradient(nx,ny,0,nx,ny,nr);
        g.addColorStop(0, nc); g.addColorStop(1,"rgba(0,0,0,0)");
        ctx.beginPath(); ctx.arc(nx,ny,nr,0,Math.PI*2); ctx.fillStyle=g; ctx.fill();
      });
  
      stars.forEach(s => {
        const tw = 0.25 + 0.75 * Math.abs(Math.sin(frame*s.spd+s.tw));
        ctx.beginPath(); ctx.arc(s.x,s.y,s.r,0,Math.PI*2);
        ctx.fillStyle = `rgba(255,255,255,${(tw*0.68).toFixed(2)})`; ctx.fill();
      });
  
      [[22,0.07],[15,0.13],[9,1]].forEach(([r,a],i) => {
        ctx.beginPath(); ctx.arc(390,42,r,0,Math.PI*2);
        ctx.fillStyle = i<2 ? `rgba(255,252,180,${a})` : "#fffde7"; ctx.fill();
      });
  
      const rot       = frame * 0.006;
      const pulse     = 0.5 + 0.5 * Math.sin(frame * 0.025);
  
      particles.forEach(p => {
        p.angle += p.spd; p.tw += p.tws;
        const x = cx + p.radius * Math.cos(p.angle);
        const y = cy + p.radius * Math.sin(p.angle) * 0.88;
        const tw = 0.28 + 0.72 * Math.abs(Math.sin(p.tw));
        ctx.beginPath(); ctx.arc(x,y,p.r,0,Math.PI*2);
        ctx.fillStyle = `rgba(${C},${(tw*0.28).toFixed(2)})`; ctx.fill();
      });
  
      for (let i = 0; i < 16; i++) {
        const a = rot*0.28 + (i/16)*Math.PI*2;
        drawPetal(cx+118*Math.cos(a), cy+118*Math.sin(a), a+Math.PI/2, 28, 0.22+0.08*pulse);
      }
      for (let i = 0; i < 12; i++) {
        const a = -rot*0.42 + (i/12)*Math.PI*2;
        drawPetal(cx+86*Math.cos(a), cy+86*Math.sin(a), a+Math.PI/2, 22, 0.30+0.10*pulse);
      }
      for (let i = 0; i < 8; i++) {
        const a = rot*0.65 + (i/8)*Math.PI*2;
        drawPetal(cx+56*Math.cos(a), cy+56*Math.sin(a), a+Math.PI/2, 16, 0.44+0.13*pulse);
      }
  
      drawMandalaRing(136, 24,  rot*0.22,  0.16+0.06*pulse);
      drawMandalaRing(152, 18, -rot*0.32,  0.13+0.05*pulse);
      drawMandalaRing(167, 12,  rot*0.45,  0.10+0.04*pulse);
  
      for (let i = 0; i < 8; i++) {
        const a = rot*0.5 + (i/8)*Math.PI*2;
        ctx.beginPath();
        ctx.moveTo(cx+18*Math.cos(a), cy+18*Math.sin(a));
        ctx.lineTo(cx+112*Math.cos(a),cy+112*Math.sin(a));
        ctx.strokeStyle = `rgba(${C},${(0.07+0.04*pulse).toFixed(3)})`;
        ctx.lineWidth = 0.8; ctx.stroke();
      }
  
      [165, 140, 112, 88, 65].forEach((r,i) => {
        ctx.beginPath(); ctx.arc(cx,cy,r,0,Math.PI*2);
        ctx.strokeStyle = `rgba(${C},${0.08+i*0.028})`; ctx.lineWidth=1; ctx.stroke();
      });
  
      const cGrd = ctx.createRadialGradient(cx,cy,0,cx,cy,105);
      cGrd.addColorStop(0,    `rgba(${C},${0.26+0.10*pulse})`);
      cGrd.addColorStop(0.45, `rgba(${C},${0.10+0.04*pulse})`);
      cGrd.addColorStop(1,    `rgba(${C},0)`);
      ctx.beginPath(); ctx.arc(cx,cy,105,0,Math.PI*2); ctx.fillStyle=cGrd; ctx.fill();
  
      drawHexagram(38, rot*0.38, 0.28+0.10*pulse);
  
      ctx.beginPath(); ctx.arc(cx,cy,52,0,Math.PI*2);
      ctx.fillStyle = `rgba(${C},0.14)`; ctx.fill();
      ctx.strokeStyle = `rgba(${C},${(0.18+0.07*pulse).toFixed(3)})`; ctx.lineWidth=7; ctx.stroke();
      ctx.strokeStyle = `rgba(195,190,255,${(0.62+0.20*pulse).toFixed(3)})`; ctx.lineWidth=1.5; ctx.stroke();
  
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.font = `bold ${40+4*pulse}px 'Space Grotesk',Arial,sans-serif`;
      ctx.shadowColor = `rgba(${C},0.90)`; ctx.shadowBlur = 22+12*pulse;
      ctx.fillStyle = `rgba(220,216,255,${(0.85+0.12*pulse).toFixed(3)})`;
      ctx.fillText("ॐ", cx, cy+2);
      ctx.shadowBlur = 7;
      ctx.font = `bold ${37+3*pulse}px 'Space Grotesk',Arial,sans-serif`;
      ctx.fillStyle = `rgba(255,255,255,${(0.45+0.18*pulse).toFixed(3)})`;
      ctx.fillText("ॐ", cx, cy+2);
      ctx.shadowBlur = 0;
  
      const orbitR = 170;
      ctx.beginPath(); ctx.arc(cx,cy,orbitR,0,Math.PI*2);
      ctx.strokeStyle=`rgba(${C},0.08)`; ctx.lineWidth=1;
      ctx.setLineDash([3,6]); ctx.stroke(); ctx.setLineDash([]);
  
      AUM.forEach((a, i) => {
        const ang = rot*0.7 + (a.idx/4)*Math.PI*2;
        const ax = cx + orbitR*Math.cos(ang), ay = cy + orbitR*Math.sin(ang);
  
        trails[i].unshift({x:ax, y:ay});
        if (trails[i].length > TRAIL) trails[i].pop();
  
        trails[i].forEach((tp, ti) => {
          const ta = (1-ti/TRAIL)*0.32, tr = a.sz*(1-ti/TRAIL)*0.48;
          if (tr > 0.3) {
            ctx.beginPath(); ctx.arc(tp.x,tp.y,tr,0,Math.PI*2);
            ctx.fillStyle = `rgba(${C},${ta.toFixed(3)})`; ctx.fill();
          }
        });
  
        const dg = ctx.createRadialGradient(ax,ay,0,ax,ay,18);
        dg.addColorStop(0, `rgba(${C},${(0.42+0.15*pulse).toFixed(3)})`); dg.addColorStop(1,"rgba(0,0,0,0)");
        ctx.beginPath(); ctx.arc(ax,ay,18,0,Math.PI*2); ctx.fillStyle=dg; ctx.fill();
  
        ctx.beginPath(); ctx.arc(ax,ay,a.sz*(0.85+0.15*pulse),0,Math.PI*2);
        ctx.fillStyle = `rgba(${C},${(0.82+0.12*pulse).toFixed(3)})`; ctx.fill();
        ctx.strokeStyle = "rgba(200,196,255,0.55)"; ctx.lineWidth=0.8; ctx.stroke();
  
        ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
        ctx.shadowColor = `rgba(${C},0.65)`; ctx.shadowBlur = 7;
        ctx.font = "bold 14px 'Space Grotesk',Arial,sans-serif";
        ctx.fillStyle = `rgba(220,216,255,0.92)`;
        ctx.fillText(a.letter, ax, ay-13);
        ctx.shadowBlur = 0;
  
        ctx.font = "7px 'JetBrains Mono','Courier New',monospace";
        ctx.fillStyle = `rgba(${C},0.50)`;
        ctx.fillText(a.label.toUpperCase(), ax, ay+19);
      });
  
      ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
      ctx.shadowColor = `rgba(${C},0.40)`; ctx.shadowBlur = 6;
      ctx.font = "13px 'Space Grotesk',Arial,sans-serif";
      ctx.fillStyle = `rgba(${C},0.38)`;
      ctx.fillText("मण्डूक्य उपनिषद्", cx, H-28);
      ctx.shadowBlur = 0;
      ctx.font = "9px 'JetBrains Mono','Courier New',monospace";
      ctx.fillStyle = `rgba(${C},0.24)`;
      ctx.fillText(c.caption, cx, H-14);
  
      frame += dtRef.current > 0 ? dtRef.current * 60 : 1;
    }
  
    drawFnRef.current = draw;
    return () => { drawFnRef.current = null; };
  }, []);
  
  useVisibleAnimation(canvasRef, (dt) => { dtRef.current = dt; drawFnRef.current?.(); }, { fps: 30 });
  
  return (
    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <canvas
        ref={canvasRef}
        style={{ display: "block", borderRadius: 8, width: "100%", height: "100%", objectFit: "contain" }}
      />
    </div>
  );
};

// ─── Main Slide ──────────────────────────────────────────────────────────────
const HTLDSlide2Inner: React.FC = () => {
const { meta, states, aum, inspiration, connection } = useSlide2();
const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((sg, i) =>
    sg.b ? <span key={i} style={{ color: sg.hi ? "#ffffff" : boldColor, fontWeight: 700 }}>{sg.t}</span>
    : sg.em ? <em key={i}>{sg.t}</em>
    : <React.Fragment key={i}>{sg.t}</React.Fragment>
  );
const fontLinkRef = useRef<HTMLLinkElement | null>(null);

useEffect(() => {
const existing = document.querySelector('link[href*="Space+Grotesk"]') as HTMLLinkElement | null;
if (!existing) {
  const link = document.createElement("link");
  link.rel  = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
  document.head.appendChild(link);
  fontLinkRef.current = link;
}
return () => {
  if (fontLinkRef.current && document.head.contains(fontLinkRef.current)) {
    document.head.removeChild(fontLinkRef.current);
    fontLinkRef.current = null;
  }
};
}, []);

const lbl: CSSProperties = {
fontSize: "clamp(9px,0.72vw,11px)", fontFamily: FONT_MONO,
color: accent, letterSpacing: 3, textTransform: "uppercase",
marginBottom: "clamp(4px,0.5vw,7px)", fontStyle: "normal",
};
const txt: CSSProperties = {
fontSize: "clamp(13px,1.05vw,15px)", fontStyle: "normal", fontFamily: FONT_BODY,
lineHeight: 1.6, color: "#cde0f2", margin: 0,
};

const rootRef = useRef<HTMLDivElement>(null);

return (
<div style={S.root} ref={rootRef}>      {/* add ref={rootRef} */}
  <SideStarField rootRef={rootRef} />   {/* add this line */}

  {/* Header row: breadcrumb (left) + slide title (right), above the horizontal line */}
  <div style={S.subtitleBar}>
    <div style={{
      fontSize: "clamp(11px,1.0vw,14px)", fontStyle: "normal",
      color: "#7aa8cc", fontFamily: FONT_BODY,
    }}>
      {meta.breadcrumbPre} &nbsp;·&nbsp;{" "}
      <span style={{ color: accent }}>{meta.breadcrumbAccent}</span>
    </div>
    <div style={{
      fontSize: "clamp(16px,1.9vw,28px)", fontStyle: "normal", fontWeight: 600,
      color: "#e8f4ff", fontFamily: FONT_DISPLAY, letterSpacing: 0.5, lineHeight: 1,
      whiteSpace: "nowrap",
    }}>
      {meta.headerPlain} <span style={{ color: accent }}>{meta.headerAccent}</span>
    </div>
  </div>

  {/* Body */}
  <div style={S.body}>

    <div style={S.content}>

      {/* Title block */}
      <div>
        <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(21px,2.4vw,34px)", fontStyle: "normal", fontWeight: 600, color: "#e8f4ff", margin: "0 0 clamp(2px,0.3vw,5px)", lineHeight: 1.15 }}>
          {meta.titlePlain} <span style={{ color: accent }}>{meta.titleAccent}</span>
        </h1>
        <p style={{ fontFamily: FONT_BODY, fontSize: "clamp(12px,1.0vw,14px)", fontStyle: "normal", fontWeight: 300, color: "#7aa8cc", margin: 0, letterSpacing: 0.3 }}>
          {meta.subtitle}
        </p>
      </div>

      {/* Inspiration */}
      <div style={{ borderBottom: "1px solid rgba(127,119,221,0.10)", paddingBottom: "clamp(5px,0.7vw,9px)" }}>
        <div style={lbl}>{meta.inspirationLabel}</div>
        <p style={txt}>{renderSegs(inspiration, "#d8d4ff")}</p>
      </div>

      {/* AUM */}
      <div style={{ borderBottom: "1px solid rgba(127,119,221,0.10)", paddingBottom: "clamp(5px,0.7vw,9px)" }}>
        <div style={lbl}>
          {meta.aumLabel} &nbsp;
          <span style={{ opacity: 0.55, textTransform: "none", letterSpacing: 1, fontSize: "clamp(9px,0.7vw,11px)" }}>· {meta.aumSub}</span>
        </div>
        <div style={{ display: "flex", gap: "clamp(10px,1.4vw,20px)", alignItems: "center" }}>
          <div style={{ display: "flex", gap: "clamp(10px,1.2vw,18px)", flexShrink: 0, alignItems: "flex-end" }}>
            {aum.map((a, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div style={{ fontSize: "clamp(18px,1.8vw,26px)", fontWeight: 600, color: i === 1 ? accent : i === 3 ? "rgba(127,119,221,0.55)" : "rgba(127,119,221,0.38)", fontFamily: FONT_DISPLAY, marginBottom: 4 }}>
                  {a.letter}
                </div>
                <div style={{ fontSize: "clamp(8px,0.68vw,10px)", fontFamily: FONT_MONO, color: "#5a7890", letterSpacing: 1, whiteSpace: "nowrap" }}>
                  {a.meaning}
                </div>
              </div>
            ))}
          </div>
          <p style={{ ...txt, color: "#9bbdd6" }}>
            {meta.aumNote}
          </p>
        </div>
      </div>

      {/* State cards */}
      <div>
        <div style={lbl}>{meta.statesLabel}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "clamp(4px,0.5vw,6px)" }}>
          {states.map((s, i) => {
            const isHTLD = s.sanskrit === slide2HighlightSanskrit;
            return (
              <div key={i} style={{ background: isHTLD ? "rgba(127,119,221,0.10)" : "rgba(255,255,255,0.02)", border: `1px solid ${isHTLD ? "rgba(127,119,221,0.35)" : "rgba(127,119,221,0.10)"}`, borderRadius: 7, padding: "clamp(5px,0.65vw,8px)", position: "relative" }}>
                <div style={{ position: "absolute", top: 6, right: 8, fontSize: "clamp(14px,1.4vw,20px)", color: isHTLD ? "rgba(127,119,221,0.75)" : "rgba(127,119,221,0.22)", fontFamily: FONT_DISPLAY, fontWeight: 600 }}>
                  {s.symbol}
                </div>
                <div style={{ fontSize: "clamp(8px,0.68vw,10px)", fontFamily: FONT_MONO, color: accent, letterSpacing: 2, marginBottom: 2 }}>
                  {s.sanskrit}
                </div>
                <div style={{ fontFamily: FONT_BODY, fontSize: "clamp(12px,1.0vw,14px)", fontStyle: "normal", fontWeight: 700, color: "#e0eeff", marginBottom: 2 }}>
                  {s.english}
                </div>
                <p style={{ fontFamily: FONT_BODY, fontSize: "clamp(11px,0.85vw,12px)", fontStyle: "normal", fontWeight: 300, lineHeight: 1.45, color: isHTLD ? "#c8c4f8" : "#9bbdd6", margin: 0 }}>
                  {s.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Callout */}
      <div style={{ background: "rgba(127,119,221,0.06)", border: "1px solid rgba(127,119,221,0.20)", borderRadius: 8, padding: "clamp(7px,0.85vw,11px) clamp(10px,1.2vw,16px)" }}>
        <div style={lbl}>{meta.connectionLabel}</div>
        <p style={{ ...txt, color: "#d0ccff" }}>{renderSegs(connection, "#e8e6ff")}</p>
      </div>
    </div>

    {/* Panel — Hindu animated canvas fills the full panel height */}
    <div style={S.panel}>
      <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <HinduCanvas />
      </div>
    </div>

  </div>

</div>
);
};

// Scale the whole slide as one unit so it looks identical across screen
// resolutions, window sizes, and browser zoom — no reflow, no clipping.
const HTLDSlide2: React.FC = () => {
  const portrait = useIsPortrait();
  return portrait
    ? <HTLDSlide2Mobile />
    : <ScaleToFit><HTLDSlide2Inner /></ScaleToFit>;
};

export default HTLDSlide2;